import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as loginApi, getCurrentUser, loginWithCaptcha, LoginWithCaptchaParams, LoginResult, RoleInfo } from '@/api/auth'
import { useLocaleStore } from '@/stores/locale'

export interface UserInfo {
  id: number
  username: string
  realName: string
  email: string
  phone: string
  roles: (string | RoleInfo)[]
  roleName?: string
  department?: string
  role?: string // 兼容旧格式
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string>(localStorage.getItem('token') || '')
  const userInfo = ref<UserInfo | null>(null)
  const permissions = ref<string[]>([]) // 权限码列表
  const loading = ref(false)

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const userRoles = computed(() => {
    // 后端返回的是 roles 数组（可能是字符串数组或对象数组）
    const user = userInfo.value
    if (!user) return []
    if (user.roles && Array.isArray(user.roles)) {
      // 提取角色代码（兼容字符串格式和对象格式）
      return user.roles.map((role: string | RoleInfo) => {
        if (typeof role === 'object' && role !== null) {
          return role.code || ''
        }
        return role
      }).filter((code: string) => code)
    }
    // 兼容旧格式：如果返回的是 role 字符串
    if (user.role) {
      return [user.role]
    }
    return []
  })

  // 判断是否拥有指定权限
  const hasPermission = (permission: string | string[]): boolean => {
    if (!permission || permission.length === 0) return true

    const permissionList = Array.isArray(permission) ? permission : [permission]

    // ADMIN角色拥有所有权限
    if (userRoles.value.includes('ADMIN')) {
      return true
    }

    return permissionList.some(p => permissions.value.includes(p))
  }

  // 判断是否拥有指定角色
  const hasRole = (role: string | string[]): boolean => {
    if (!role || role.length === 0) return true

    const roleList = Array.isArray(role) ? role : [role]

    // ADMIN角色拥有所有角色
    if (userRoles.value.includes('ADMIN')) {
      return true
    }

    return roleList.some(r => userRoles.value.includes(r))
  }

  // 根据登录返回的 preferredLanguage 初始化前端 locale
  const applyPreferredLanguage = (preferredLanguage?: string) => {
    if (preferredLanguage === 'zh-CN' || preferredLanguage === 'en') {
      const localeStore = useLocaleStore()
      localeStore.changeLanguage(preferredLanguage)
    }
  }

  // Actions
  const login = async (username: string, password: string) => {
    loading.value = true
    try {
      const res: LoginResult = await loginApi(username, password)
      token.value = res.token
      localStorage.setItem('token', res.token)
      userInfo.value = res.user
      // 存储权限列表
      permissions.value = res.permissions || []
      // 按用户偏好初始化语言
      applyPreferredLanguage(res.user?.preferredLanguage)
      return res
    } catch (error: any) {
      // 重新抛出错误，让调用者处理
      throw error
    } finally {
      loading.value = false
    }
  }

  const loginWithCaptchaAction = async (params: LoginWithCaptchaParams): Promise<LoginResult> => {
    loading.value = true
    try {
      const res: LoginResult = await loginWithCaptcha(params)
      token.value = res.token
      localStorage.setItem('token', res.token)
      userInfo.value = res.user
      // 存储权限列表
      permissions.value = res.permissions || []
      // 按用户偏好初始化语言
      applyPreferredLanguage(res.user?.preferredLanguage)
      return res
    } catch (error: any) {
      // 重新抛出错误，让调用者处理
      throw error
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    token.value = ''
    userInfo.value = null
    permissions.value = []
    localStorage.removeItem('token')
  }

  const fetchUserInfo = async () => {
    if (!token.value) return
    try {
      const res = await getCurrentUser()
      userInfo.value = res
      if (res.permissions) {
        permissions.value = res.permissions
      }
    } catch (error) {
      logout()
      throw error
    }
  }

  const initAuth = async () => {
    if (token.value) {
      await fetchUserInfo()
    }
  }

  return {
    token,
    userInfo,
    permissions,
    loading,
    isLoggedIn,
    userRoles,
    hasPermission,
    hasRole,
    login,
    loginWithCaptcha: loginWithCaptchaAction,
    logout,
    fetchUserInfo,
    initAuth
  }
})
